import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useT, LanguageSwitcher } from "@/lib/i18n";
import { CheckCircle2, XCircle, Mail, ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import { API_BASE } from "@/lib/apiBase";

const API = API_BASE;

export default function VerifyEmailPage() {
  const { t } = useT();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [detail, setDetail] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setDetail(t("ve.no_token") || "Kein Token gefunden.");
      return;
    }
    axios.post(`${API}/auth/verify-email`, { token }).then(
      () => setState("success"),
      (err) => {
        setState("error");
        const raw = err?.response?.data?.detail;
        const msg =
          raw === "token_expired"
            ? (t("ve.expired") || "Link abgelaufen.")
            : raw === "token_already_used"
            ? (t("ve.used") || "Link wurde bereits verwendet.")
            : raw === "invalid_token"
            ? (t("ve.invalid") || "Link ist ungültig.")
            : (t("ve.failed") || "Bestätigung fehlgeschlagen.");
        setDetail(msg);
      }
    );
  }, [token, t]);

  return (
    <div className="min-h-screen bg-radial-neon flex items-center justify-center p-6" data-testid="verify-email-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <Link to="/login" className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white">
            <ArrowLeft size={14} /> FicoPilot
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="mb-8 flex justify-center">
          <Logo to="/" />
        </div>
        <div className="rounded-2xl border border-white/5 bg-zinc-900/60 p-6 text-center">
          {state === "verifying" && (
            <>
              <Mail className="mx-auto text-neon mb-3 animate-pulse" size={40} />
              <h2 className="font-display text-2xl font-black">{t("ve.verifying") || "Bestätige E-Mail…"}</h2>
              <p className="mt-2 text-sm text-zinc-400">{t("ve.verifying_sub") || "Das dauert nur einen Moment."}</p>
            </>
          )}
          {state === "success" && (
            <div data-testid="verify-email-success">
              <CheckCircle2 className="mx-auto text-emerald-400 mb-3" size={48} />
              <h2 className="font-display text-2xl font-black">{t("ve.success") || "E-Mail bestätigt"}</h2>
              <p className="mt-2 text-sm text-zinc-400">
                {t("ve.success_sub") || "Dein Account ist jetzt vollständig aktiviert. Viel Spaß, Pilot."}
              </p>
              <Link to="/app">
                <Button className="mt-5 h-11 w-full rounded-xl bg-neon text-black font-bold hover:bg-neon-hover">
                  {t("ve.go_app") || "Zur App"}
                </Button>
              </Link>
            </div>
          )}
          {state === "error" && (
            <div data-testid="verify-email-error">
              <XCircle className="mx-auto text-red-400 mb-3" size={48} />
              <h2 className="font-display text-2xl font-black">{t("ve.error") || "Konnte nicht bestätigen"}</h2>
              <p className="mt-2 text-sm text-zinc-400">{detail}</p>
              <Link to="/app/profile">
                <Button className="mt-5 h-11 w-full rounded-xl bg-white/10 text-white font-semibold hover:bg-white/15">
                  {t("ve.resend") || "Neuen Link anfordern"}
                </Button>
              </Link>
            </div>
          )}
        </div>
        <Footer variant="compact" />
      </div>
    </div>
  );
}
